import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as GET_COMMUNITY_QUERY_KEY } from './useGetCommunity';

export type ApiRequest_Remove_Member = {
  communityId: number;
  membershipId: number;
};

export type ApiResponse_Remove_Member = {
  message: string;
  membership_id: number;
};

const METHOD = 'POST';
const ENDPOINT = 'communities';

export const useRemoveMember = (options?: {
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Remove_Member) => {
      const data = await handleApiCall<
        ApiResponse_Remove_Member,
        Record<string, never>
      >(
        session,
        METHOD,
        getResolvedEndpoint(
          `${ENDPOINT}/${params.communityId}/members/${params.membershipId}/remove`,
          {}
        ),
        {}
      );

      return { ...data, communityId: params.communityId };
    },
    onSuccess: (data) => {
      enqueueSnackbar('Member removed successfully', { variant: 'success' });

      // Invalidate community query to refresh the member list
      queryClient.invalidateQueries({
        queryKey: GET_COMMUNITY_QUERY_KEY({ id: data.communityId }),
      });

      options?.onSuccess?.();
    },
    onError: (error) => {
      enqueueSnackbar('Failed to remove member', { variant: 'error' });

      // eslint-disable-next-line no-console
      console.error('Remove member error:', error);

      options?.onError?.();
    },
  });
};